namespace app.documentos {
    
    import IDirective = angular.IDirective;
    import IDirectiveFactory = angular.IDirectiveFactory;
    import IScope = angular.IScope;
    import IAugmentedJQuery = angular.IAugmentedJQuery;
    import IAttributes = angular.IAttributes;
    import ITimeoutService = angular.ITimeoutService;
    
    // Funções utilitárias
    
    function key(scope: OnlyofficeScope, readOnly: boolean): string {
        if (readOnly) {
            let time = new Date().getTime();
            
            if (!!scope.config.document.id) {
                return `${scope.contexto}-${scope.config.document.id}-${time}`;
            } else {
                return `${scope.contexto}-${scope.config.document.key}-${time}`;
            }   
        }
        return scope.config.document.key;
    }
    
    function getDocumentType(ext : string): string {
        if (".docx.doc.odt.rtf.txt.html.htm.mht.pdf.djvu.fb2.epub.xps".indexOf(ext) != -1)
            return "text";
        if (".xls.xlsx.ods.csv".indexOf(ext) != -1)
            return "spreadsheet";
        if (".pps.ppsx.ppt.pptx.odp".indexOf(ext) != -1)
            return "presentation";
        return null;
    }
    
    export interface OnlyofficeScope extends IScope {
        config: DocumentConfig;
        readOnly: boolean;
        docEditor: DocsAPI.OnlyofficeDocEditor;
        ready: boolean;
        historyEnabled: boolean;
        contexto: string;
        api: OnlyofficeDirectiveApi;
        configOverride: DocsAPI.OnlyofficeConfig;
    }
    
    export interface DocumentConfig {
        document: Document;
        editorConfig : { 
            lang: string,
            customization: {
                about: boolean,
                chat: boolean,
                compactToolbar: boolean
            },
            mode: string,
            user: User,
            plugins: DocsAPI.OnlyofficePluginsConfig
        }
    }
    
    export interface Document {
        id?: number | string;
        src: string;
        key: string;
        name: string;
        callbackUrl: string;
        temp: boolean;
    }
    
    export interface User {
        id: string;
        name: string;
    }
    
    export interface OnlyofficeDirectiveApi {
        plugin(name: string): OnlyofficePluginApi;
    }

    export interface OnlyofficePluginApi {
        call(method: string, params?): ng.IPromise<{}>;
    }
    
    class OnlyofficeDirective implements IDirective {

        public restrict: string = 'AE';
        public template: string = '<div id="{{placeholderId}}"></div>';
    
        public scope: Object = {
            config: "=onlyoffice",
            readOnly: "=readOnly",
            historyEnabled: "=historyEnabled",
            contexto: "@",
            api: "=?",
            configOverride: "="
        };

        constructor(private $timeout: ITimeoutService, private onlyofficeService: OnlyofficeService,
                private $q: ng.IQService) {
            
        }
        
        public link($scope: OnlyofficeScope, el: IAugmentedJQuery, attrs: IAttributes) {
            $scope.placeholderId = "onlyoffice-editor-" + pseudorandomId();
            $scope.$watch('config.document.src', (newVal) => {
                if (!newVal) {
                    return;
                }
                let editorConfig = $scope.config.editorConfig;
                let documentConfig = $scope.config.document;
                 
                let readOnly = editorConfig.mode === "view" ? true : $scope.readOnly; 
                
                let docUrl : string = documentConfig.src;
                let docTitle = documentConfig.name || docUrl;
                let docKey = key($scope, readOnly);
                let docType = docUrl.split('?')[0].substring(docUrl.lastIndexOf(".") + 1).trim().toLowerCase();
                let callbackUrl = documentConfig.callbackUrl;
                let historyEnabled = $scope.historyEnabled ? $scope.historyEnabled : false;
                
                if (!editorConfig.plugins) {
                    let pluginsConfig: DocsAPI.OnlyofficePluginsConfig = {
                        autostart: [],
                        pluginsData: [],
                        pluginsParams: []
                    };
                    editorConfig.plugins = pluginsConfig;
                }
                
                this.onlyofficeService.recuperarUrlPlugins($scope.contexto).then((urlPlugins) => {
                    this.prependDefaultPlugins(urlPlugins, editorConfig.plugins);
                    
                    let config: DocsAPI.OnlyofficeConfig = {
                        type : "desktop",
                        width : "100%",
                        height : "100%",
                        documentType : "text",
                        document : {
                            fileType : docType,
                            key : docKey,
                            title : docTitle,
                            url : docUrl,
                            permissions : {
                                edit : !readOnly,
                                download : true,
                                reader : true,
                                print: true
                            },
                        },
                        editorConfig : {
                            callbackUrl: callbackUrl,
                            lang: editorConfig.lang,
                            mode : editorConfig.mode,
                            customization: editorConfig.customization,
                            user: {
                                id: editorConfig.user.id,
                                name: editorConfig.user.name
                            },
                            plugins: editorConfig.plugins
                        },
                        events : {
                            onAppReady : function() {
                                this.$timeout(function() {
                                    $scope.ready = true;
                                }, 0);
                            }
                        }
                    };
                    
                    this.$q.when(historyEnabled).then((historyEnabled) => {
                        if (historyEnabled) {
                            return this.onlyofficeService.isHistoryEnabled($scope.contexto).then((enabled) => {
                                if (enabled) {
                                    config.events.onRequestHistory = () => {
                                        this.onlyofficeService.recuperarHistorico(documentConfig.id, $scope.contexto).then((history) => {
                                            let historyData = {
                                                currentVersion: "0",
                                                history: []    
                                            };
                                            if (!!history && history.length > 0) {
                                                let version = _.maxBy(history, h => parseInt(h.version)).version;
                                                let serverVersion = DocsAPI.DocEditor.version();
                                                history.forEach(h => {
                                                    h.serverVersion = serverVersion;
                                                });
                                                historyData.currentVersion = version;
                                                historyData.history = history;
                                            }
                                            $scope.docEditor.refreshHistory(historyData); 
                                        });
                                        
                                    };
                                    config.events.onRequestHistoryData = (event) => {
                                        var version = event.data;
                                        var key1 = Math.random().toString(36).substr(2, 9);
                                        var key2 = Math.random().toString(36).substr(2, 9);
                                        
                                        this.onlyofficeService.recuperarUrlChanges(documentConfig.id, version, $scope.contexto)
                                                .then((urlChanges) => {
                                            this.$q.all([this.onlyofficeService.recuperarUrlDocumentVersion(documentConfig.id,
                                                    version,  $scope.contexto), this.onlyofficeService.recuperarUrlDocumentVersion(documentConfig.id,
                                                    version - 1,  $scope.contexto)]).then((urls) => {
                                                this.onlyofficeService.existeChanges(documentConfig.id, version,
                                                        $scope.contexto).then((existe) => {
                                                    let historyData;
                                                    if (existe) {
                                                        historyData = {
                                                            changesUrl:  urlChanges,
                                                            key: key1,
                                                            previous: {
                                                                key: key2,
                                                                url: urls[1],
                                                            },
                                                            url: urls[0],
                                                            version: version
                                                        };
                                                    } else {
                                                        historyData = {
                                                            key: key1,
                                                            url: urls[0],
                                                            version: version
                                                        };
                                                    }
                                                    if (this.onlyofficeService.isSecure()) {
                                                        this.onlyofficeService.generateJwtTokenHistoryData(historyData, $scope.contexto).then((tokenObj) => {
                                                            historyData.token = tokenObj.token;
                                                            $scope.docEditor.setHistoryData(historyData);
                                                        });
                                                    } else {
                                                        $scope.docEditor.setHistoryData(historyData);
                                                    }
                                                });
                                            });
                                        });
                                    };
                                    config.events.onRequestHistoryClose = () => {
                                        this.fecharHistoricoRecarregar($scope, config);
                                    };
                                    config.events.onRequestRestore = (event) => {
                                        let version = event.data.version;
                                        this.onlyofficeService.restaurarVersao(documentConfig.id, version, $scope.contexto)
                                            .then(() => {
                                                this.fecharHistoricoRecarregar($scope, config);
                                            });
                                    };
                                }
                            });
                        }
                    }).finally(() => {
                        this.loadEditor($scope, config);
                        $scope.$on("$destroy", () => {
                            $scope.docEditor.destroyEditor();
                        });
                    });
                
                });
            });
        }
        
        private fecharHistoricoRecarregar($scope: OnlyofficeScope, config: DocsAPI.OnlyofficeConfig) {
            $scope.docEditor.destroyEditor();
            this.onlyofficeService.excluirNumeroEdicao($scope.config.document.id, false, $scope.contexto)
                .then(() => {
                    this.onlyofficeService.gerarNumeroEdicao($scope.config.document.id, false, $scope.contexto)
                        .then((edicao) => {
                            config.document.key = edicao.numero;
                            this.loadEditor($scope, config);
                        });
                });
        }
        
        private loadEditor($scope: OnlyofficeScope, config: DocsAPI.OnlyofficeConfig) {
            let overriddenConfig = this.overrideConfig(config, $scope.configOverride);
            if (this.onlyofficeService.isSecure()) {
                this.onlyofficeService.generateJwtToken(overriddenConfig, $scope.contexto).then((tokenObj) => {
                    overriddenConfig.token = tokenObj.token;
                    this.loadOnlyofficeDirectiveApi($scope, overriddenConfig);
                    $scope.docEditor = new DocsAPI.DocEditor($scope.placeholderId, overriddenConfig);
                });
            } else {
                this.loadOnlyofficeDirectiveApi($scope, overriddenConfig);
                $scope.docEditor = new DocsAPI.DocEditor($scope.placeholderId, overriddenConfig);
            }
        }
        
        private overrideConfig(config: DocsAPI.OnlyofficeConfig, configOverride: DocsAPI.OnlyofficeConfig) {
            return angular.merge({}, config, configOverride);
        }

        private prependDefaultPlugins(urlPlugins: string, plugins: DocsAPI.OnlyofficePluginsConfig): DocsAPI.OnlyofficePluginsConfig {
            plugins.pluginsData.unshift(urlPlugins + "/move-cursor-start/config.json");
            plugins.pluginsData.unshift(urlPlugins + "/content-control-blocker/config.json");
            plugins.pluginsData.unshift(urlPlugins + "/enable-content-controls/config.json");

            plugins.pluginsData.unshift(urlPlugins + "/autoloader/config.json");
            plugins.pluginsParams.unshift({
                id: "autoloader",
                params: {
//                    // Exemplo de como carregar um "addon" via autoloader, passando parâmetros.
//                    "hello-world": {
//                        type: "addon",
//                        params: {
//                            msg: "Hello, World!"
//                        }
//                    }
                }
            });

            plugins.pluginsParams.unshift({
                id: "shared-params",
                params: {
                    "onlyoffice-version": DocsAPI.DocEditor.version()
                }
            });
            
            return plugins;
        }
        
        private loadOnlyofficeDirectiveApi($scope: OnlyofficeScope, config: DocsAPI.OnlyofficeConfig) {
            $scope.api = new OnlyofficeDirectiveApiImpl($scope, config, this.$q);
        }
        
        public static factory(): IDirectiveFactory {
            return ($timeout: ITimeoutService, onlyofficeService: OnlyofficeService, $q: ng.IQService) => {
                "ngInject";
                return new OnlyofficeDirective($timeout, onlyofficeService, $q);
            };
        }
    
    }
    
    function pseudorandomId() {
        return new Date().getTime().toString() + Math.floor(Math.random() * 65536); 
    }

    angular.module('app.documentos').directive('onlyoffice', OnlyofficeDirective.factory());
}